/**
 * AI Recruit - Source Selector Component
 * Reusable dropdown for selecting job or resume sources
 */

import React from 'react'
import clsx from 'clsx'
import { useQuery } from '@tanstack/react-query'
import { fetchSources } from '../../services/sourceApi'
import LoadingSpinner from './LoadingSpinner'

interface SourceSelectorProps {
  type: 'job' | 'resume'
  value: string | null
  onChange: (sourceId: string) => void
  error?: string
  required?: boolean
  disabled?: boolean
  label?: string
  className?: string
}

const SourceSelector: React.FC<SourceSelectorProps> = ({
  type,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  label,
  className
}) => {
  // Fetch sources using TanStack Query
  const { data: sources, isLoading, isError } = useQuery({
    queryKey: ['sources', type],
    queryFn: () => fetchSources(type),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Sort sources alphabetically by name
  const sortedSources = sources ? [...sources].sort((a, b) => a.name.localeCompare(b.name)) : []

  return (
    <div className={clsx('space-y-1', className)}>
      {label && (
        <label
          htmlFor="source-select"
          className="block text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <select
          id="source-select"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || isLoading}
          className={clsx(
            'block w-full rounded-md shadow-sm sm:text-sm',
            'focus:ring-primary-500 focus:border-primary-500',
            'disabled:bg-gray-100 disabled:cursor-not-allowed',
            error
              ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300',
            isLoading && 'pr-10'
          )}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? 'source-error' : undefined}
        >
          <option value="">Select a source</option>
          {sortedSources.map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>

        {isLoading && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-8 pointer-events-none">
            <LoadingSpinner size="sm" variant="gray" />
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600" id="source-error">
          {error}
        </p>
      )}

      {isError && (
        <p className="text-sm text-red-600">
          Failed to load sources. Please try again.
        </p>
      )}
    </div>
  )
}

export default SourceSelector
